const AUTH_METHOD_KEY = "auth_method"
const TOKEN_KEY = "token"
const IDENTITY_AUTH_METHOD = "identity"

const GRAPHQL_AUTH_ERRORS = [
  "Authentication failed",
  "Authentication required for identity-based auth",
  "Invalid identity token"
]

let refreshPromise = null
let refreshCircuitOpen = false
let updateTokenCallback = null
let logoutCallback = null

export function configureIdentityTokenRefresh(handlers) {
  if (handlers.updateToken) {
    updateTokenCallback = handlers.updateToken
  }
  if (handlers.logout) {
    logoutCallback = handlers.logout
  }
}

/**
 * Re-arms token refreshing after a successful login.
 *
 * Call this whenever a new session is established, so a page-session that
 * previously burned its refresh circuit can refresh again without a full
 * page reload.
 */
export function resetIdentityTokenRefresh() {
  refreshCircuitOpen = false
  refreshPromise = null
}

export function isIdentityAuthMode() {
  return localStorage.getItem(AUTH_METHOD_KEY) === IDENTITY_AUTH_METHOD
}

export function getIdentityRefreshUrl() {
  if (!API_IDENTITY) {
    return null
  }

  const baseUrl = API_IDENTITY.endsWith("/") ? API_IDENTITY.slice(0, -1) : API_IDENTITY

  return `${baseUrl}/keycloak/refresh/`
}

function persistAccessToken(token) {
  if (updateTokenCallback) {
    updateTokenCallback(token)
    return
  }

  localStorage.setItem(TOKEN_KEY, JSON.stringify(token))
}

function buildRefreshHeaders() {
  const headers = {
    "Content-Type": "application/json"
  }

  if (TENANT) {
    headers.tenant = TENANT
  }

  return headers
}

/**
 * Single owner of the "refresh has definitively failed" path.
 *
 * Opens the circuit before logging out so logout()'s resetStore refetches
 * cannot re-enter refreshAccessToken().
 */
function notifyRefreshFailure(error) {
  refreshCircuitOpen = true

  if (logoutCallback) {
    logoutCallback()
  }

  return Promise.reject(error)
}

/**
 * Refreshes the Identity access token, de-duplicating concurrent callers.
 */
export function refreshAccessToken() {
  if (refreshCircuitOpen) {
    return Promise.reject(new Error("Identity refresh already failed for this session"))
  }

  if (!isIdentityAuthMode()) {
    return Promise.reject(new Error("Identity auth is not active"))
  }

  const refreshUrl = getIdentityRefreshUrl()
  if (!refreshUrl) {
    return Promise.reject(new Error("API_IDENTITY is not configured"))
  }

  if (!refreshPromise) {
    refreshPromise = fetch(refreshUrl, {
      method: "POST",
      credentials: "include",
      headers: buildRefreshHeaders()
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error(`Identity refresh failed with status ${response.status}`)
        }

        return response.json()
      })
      .then(function(data) {
        if (!data.access_token) {
          throw new Error("Identity refresh response missing access_token")
        }

        persistAccessToken(data.access_token)
        return data.access_token
      })
      .catch(notifyRefreshFailure)
      .finally(function() {
        refreshPromise = null
      })
  }

  return refreshPromise
}

export function shouldAttemptTokenRefresh(config) {
  if (!config || config._identityRetried || config.skipIdentityTokenRefresh) {
    return false
  }

  if (config.url && config.url.indexOf("/keycloak/refresh/") !== -1) {
    return false
  }

  return true
}

export function isGraphQLAuthError(graphQLErrors) {
  if (!graphQLErrors || !graphQLErrors.length) {
    return false
  }

  return graphQLErrors.some(function(error) {
    return GRAPHQL_AUTH_ERRORS.indexOf(error.message) !== -1
  })
}
